import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { RouterProvider as AriaRouterProvider } from "react-aria-components";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { router } from "./router";
import { queryClient } from "./lib/queryClient";
import { setSessionExpiredHandler } from "./lib/apiClient";
import { useAuthStore } from "./lib/authStore";
import { ToastProvider } from "./components/application/notifications/toast-provider";
import i18n from "./i18n";
import { initTheme } from "./stores/themeStore";
import "./styles.css";

initTheme();

// Globální 401 handler — když libovolný business endpoint vrátí 401
// (typicky kvůli expirované session), zresetuje auth store a tvrdě
// přesměruje na /login s návratovou URL. Vyhne se smyčce když je user
// už na /login. Auth-flow paths (login/me/refresh) jsou vyloučené
// na úrovni apiClient.
setSessionExpiredHandler((currentPath) => {
  useAuthStore.getState().setAnonymous();
  if (typeof window === "undefined") return;
  if (currentPath.startsWith("/login")) return;
  const next = encodeURIComponent(currentPath);
  window.location.assign(`/login?next=${next}`);
});

/**
 * Entrypoint — Phase 8 shell.
 *
 * Provider order (outside → inside):
 *   1. QueryClientProvider → TanStack Query cache.
 *   2. ToastProvider       → useToast hook globally available.
 *   3. RouterProvider      → TanStack Router file-based routing.
 *
 * StrictMode wraps everything pro double-render assertion (dev only).
 */
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          {/* react-aria RouterProvider → UUI komponenty (sidebar nav, links na
              react-aria) dělají SPA navigaci přes TanStack Router místo full reload. */}
          <AriaRouterProvider navigate={(to) => void router.navigate({ to })}>
            <RouterProvider router={router} />
          </AriaRouterProvider>
        </ToastProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
